import { useState } from 'react';
import { Unlock, AlertTriangle } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';

interface CIUnlockModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (reason: string, createChangeOrder: boolean) => void;
  ciName: string;
  loading?: boolean;
  /** The engagement's open draft change order: the checkbox then adds to it instead of creating one. */
  openChangeOrder?: { id: string; changeOrderId: string } | null;
}

export function CIUnlockModal({
  open,
  onClose,
  onConfirm,
  ciName,
  loading = false,
  openChangeOrder = null,
}: CIUnlockModalProps) {
  const [reason, setReason] = useState('');
  const [createChangeOrder, setCreateChangeOrder] = useState(false);

  const handleConfirm = () => {
    if (!reason.trim()) {
      return; // Reason is required
    }
    onConfirm(reason, createChangeOrder);
    setReason(''); // Reset
    setCreateChangeOrder(false);
  };

  const handleCancel = () => {
    setReason(''); // Reset
    setCreateChangeOrder(false);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleCancel}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Unlock className="size-5 text-orange-600" />
            Unlock Configuration Item?
          </DialogTitle>
          <DialogDescription>
            Unlock <strong>{ciName}</strong> to allow client to make changes
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Warning */}
          <Alert variant="default" className="border-orange-200 bg-orange-50">
            <AlertTriangle className="size-4 text-orange-600" />
            <AlertTitle className="text-orange-900">Impact of Unlocking</AlertTitle>
            <AlertDescription className="text-orange-800 text-sm mt-2">
              <p>
                This will allow the client to make changes to requirements. This may impact the implementation timeline and could require rework.
              </p>
            </AlertDescription>
          </Alert>

          {/* Reason Field (Required) */}
          <div className="space-y-2">
            <Label htmlFor="unlock-reason">
              Reason for unlocking <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="unlock-reason"
              placeholder="e.g., 'Client requested change to approval workflow based on new business requirements'"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              className="resize-none"
              required
            />
            <p className="text-xs text-slate-500">
              This will be recorded in the unlock history and visible to all team members.
            </p>
          </div>

          {/* Change Order Option */}
          <div className="flex items-start space-x-3 p-3 border rounded-lg">
            <Checkbox
              id="create-change-order"
              checked={createChangeOrder}
              onCheckedChange={(checked) => setCreateChangeOrder(checked === true)}
            />
            <div className="grid gap-1.5 leading-none">
              <label
                htmlFor="create-change-order"
                className="text-sm cursor-pointer"
              >
                {openChangeOrder ? `Add to open change order ${openChangeOrder.changeOrderId}` : 'Create change order for scope changes'}
              </label>
              <p className="text-xs text-slate-500">
                {openChangeOrder
                  ? `If the client is requesting changes that impact the original scope, check this box to add this item to the draft change order ${openChangeOrder.changeOrderId}.`
                  : 'If the client is requesting changes that impact the original scope, check this box to create a change order for tracking and approval.'}
              </p>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={loading || !reason.trim()}
            className="bg-orange-600 hover:bg-orange-700"
          >
            {loading && <span className="mr-2">...</span>}
            {!loading && <Unlock className="size-4 mr-2" />}
            Unlock CI
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
